/**
 * ModularInformantSelfDataContext.js
 *
 * Service-user context for modular informants (Adult Autism, CYP ADHD, and
 * future subcollection-based pathways). Informants log in with
 * userType=Informant and a modular assessmentModule claim; their questionnaire
 * answers live in the pathway-namespaced assignment tree:
 *
 *   informants/{informantUid}/patients/{patientUid}/{collection}/{assessmentDocId}/formInstances/{formKey}
 *
 * The profile doc at informants/{informantUid} holds activeAssignments — a
 * denormalized index of linked patients plus patientFirstName for dashboard
 * copy. The patient name is resolved from this snapshot (no direct patient doc
 * read) to avoid exposing the full patient record to informant auth tokens.
 *
 * ADHD informants continue using PatientSelfDataContext (monolithic patients/{uid}
 * doc) — this context activates for any modular assessmentModule.
 */
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { db } from '../firebase';
import { doc, getDoc, getDocs } from 'firebase/firestore';
import { useAuth } from '../hooks/useAuth';
import { useModularFormInstances } from '../hooks/useModularFormInstances';
import {
  getModularInformantFormInstancesCollectionRef,
  resolveModularInformantLinkedPatientId,
  resolvePatientFirstNameForInformant,
} from '../utils/informantFirestorePaths';
import { isModularModule, getModuleCollection, getModuleAssessmentDocId } from '../constants/assessmentModules';
import { assessmentTypeFromModule } from '../analytics/serviceUserFormEvents';
import { isCypInformantAssignmentLocked } from '../utils/formLockingUtils';

export const ModularInformantSelfDataContext = createContext();

const LOCALSTORAGE_KEY = 'td_modular_self_informant_data';

export const ModularInformantSelfDataProvider = ({ children }) => {
  const { user: currentUser, claims } = useAuth();

  const assessmentModule = claims?.assessmentModule;

  const isModularInformant =
    claims?.accountType === 'Service User' &&
    claims?.userType === 'Informant' &&
    isModularModule(assessmentModule);

  const [linkedPatientId, setLinkedPatientId] = useState(null);
  const [patientFirstName, setPatientFirstName] = useState('');
  const linkedPatientIdRef = useRef(null);

  const fetchData = useCallback(async () => {
    const informantId = currentUser.uid;
    const profileRef = doc(db, 'informants', informantId);
    const profileSnap = await getDoc(profileRef);

    if (!profileSnap.exists()) {
      throw new Error('Informant profile not found');
    }

    const profile = profileSnap.data();
    const patientId = resolveModularInformantLinkedPatientId(profile, assessmentModule);

    linkedPatientIdRef.current = patientId;
    setLinkedPatientId(patientId);

    if (!patientId) {
      setPatientFirstName('');
      return { header: profile, formInstances: {} };
    }

    const formsSnap = await getDocs(
      getModularInformantFormInstancesCollectionRef(informantId, patientId, assessmentModule),
    );

    const formInstancesData = {};
    formsSnap.forEach((docSnap) => {
      formInstancesData[docSnap.id] = docSnap.data();
    });

    const firstName = resolvePatientFirstNameForInformant(profile, patientId, assessmentModule);
    setPatientFirstName(firstName);

    return {
      header: {
        ...profile,
        linkedPatientId: patientId,
        patientFirstName: firstName,
      },
      formInstances: formInstancesData,
    };
  }, [currentUser, assessmentModule]);

  const getFormInstanceDocRef = useCallback((formKey) => {
    const patientId = linkedPatientIdRef.current;
    if (!currentUser?.uid || !patientId) {
      throw new Error('Informant assignment not ready for writes');
    }
    const col = getModuleCollection(assessmentModule) || 'adult_autism';
    const docId = getModuleAssessmentDocId(assessmentModule) || 'assessment_001';
    return doc(
      db,
      'informants', currentUser.uid,
      'patients', patientId,
      col, docId,
      'formInstances', formKey,
    );
  }, [currentUser?.uid, assessmentModule]);

  // The claim is 'adultAutism' / 'cypAdhd'; the events carry the canonical
  // enum ('autism' / 'cyp_adhd') and baseFields drops any event whose
  // assessment_type is not one of those. Passing the raw claim through
  // silently stopped every service-user form event on this pathway.
  const informantAnalyticsContext = useMemo(
    () => ({
      role: 'informant',
      assessmentType: assessmentTypeFromModule(assessmentModule),
      subjectId: linkedPatientId,
    }),
    [linkedPatientId, assessmentModule],
  );

  const {
    header,
    formInstances,
    loading,
    error,
    fetchEntityData,
    updateFormAnswers,
    clearData,
  } = useModularFormInstances({
    enabled: isModularInformant && !!currentUser,
    entityId: currentUser?.uid || null,
    localStorageKey: LOCALSTORAGE_KEY,
    fetchData,
    getFormInstanceDocRef,
    analyticsContext: informantAnalyticsContext,
  });

  // Mirror linkedPatientId from header after fetch when state was not set yet.
  useEffect(() => {
    if (header?.linkedPatientId && header.linkedPatientId !== linkedPatientId) {
      setLinkedPatientId(header.linkedPatientId);
    }
    if (header?.patientFirstName && !patientFirstName) {
      setPatientFirstName(header.patientFirstName);
    }
  }, [header, linkedPatientId, patientFirstName]);

  const fetchInformantData = fetchEntityData;

  // CYP ADHD form lock: the server-side sync writes the effective lock onto
  // the informant's own profile at activeAssignments.cyp_adhd.{patientId}.formsLocked.
  // This derived value is consumed by GenericFormPage and ModularInformantDashboard
  // to show the locked card / notice when the clinician has generated a CI/SSI report.
  // Adult Autism informants are unaffected (no cyp_adhd key on their profile).
  const formsLocked = useMemo(
    () => isCypInformantAssignmentLocked(header, linkedPatientId),
    [header, linkedPatientId],
  );

  return (
    <ModularInformantSelfDataContext.Provider
      value={{
        header,
        formInstances,
        linkedPatientId,
        patientFirstName,
        loading,
        error,
        formsLocked,
        fetchInformantData,
        updateFormAnswers,
        clearInformantData: clearData,
      }}
    >
      {children}
    </ModularInformantSelfDataContext.Provider>
  );
};
